import type { ImageAdapter } from "./ImageAdapter";
import { Viewport } from "../pixel-accessor";
import type { Color } from "../types";

/**
 * Abstract image class for ImageAdapter implementation.
 */
export abstract class AbstractImageAdapter<ResourceType>
  implements ImageAdapter<ResourceType>
{
  /**
   * Image width.
   */
  readonly width: number;

  /**
   * Image height.
   */
  readonly height: number;

  /**
   * Image virtual viewport.
   */
  protected viewport: Viewport;

  /**
   * Image background color.
   */
  protected backgroundColor: Color;

  /**
   * Image quantum range (per channel).
   */
  protected quantumRange: number;

  /**
   * AbstractImageAdapter constructor.
   *
   * @param width Image width.
   * @param height Image height.
   */
  protected constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.viewport = new Viewport(0, 0, this.width - 1, this.height - 1);
    this.backgroundColor = [0, 0, 0, 0];
    this.quantumRange = 255;
  }

  /**
   * @inheritDoc
   */
  abstract getPixelColor(x: number, y: number): Color;

  /**
   * @inheritDoc
   */
  abstract setPixelColor(x: number, y: number, color: Color): void;

  /**
   * @inheritDoc
   */
  abstract getAverageColor(): Color;

  /**
   * Returns resized instance of self.
   *
   * @param width New width.
   * @param height New height.
   * @returns New resized instance.
   */
  protected abstract resize(
    width: number,
    height: number
  ): ImageAdapter<ResourceType>;

  /**
   * @inheritDoc
   */
  abstract getResource(): ResourceType;

  /**
   * Prepares blank image for ImageAdapter.getBlank method.
   *
   * @param width Image width.
   * @param height Image height.
   */
  protected abstract prepareBlank(
    width: number,
    height: number
  ): ImageAdapter<ResourceType>;

  /**
   * @inheritDoc
   */
  abstract commit(): void;

  /**
   * @inheritDoc
   */
  getViewport(): Viewport {
    return this.viewport;
  }

  /**
   * @inheritDoc
   */
  setViewport(viewport: Viewport): this {
    this.viewport = viewport;
    return this;
  }

  /**
   * @inheritDoc
   */
  getBackgroundColor(): Color {
    return this.backgroundColor;
  }

  /**
   * @inheritDoc
   */
  setBackgroundColor(color: Color): this {
    this.backgroundColor = color;
    return this;
  }

  /**
   * @inheritDoc
   */
  getQuantumRange(): number {
    return this.quantumRange;
  }

  /**
   * @inheritDoc
   */
  getBlank(viewport: Viewport): ImageAdapter<ResourceType> {
    const blank = this.prepareBlank(viewport.getWidth(), viewport.getHeight());
    blank.setViewport(viewport);
    return this.duplicateProps(blank);
  }

  /**
   * @inheritDoc
   */
  scale(scale: number): ImageAdapter<ResourceType> {
    const viewport = this.viewport.clone();
    viewport.scale(scale);

    const resized = this.resize(viewport.getWidth(), viewport.getHeight());
    resized.setViewport(viewport);
    return this.duplicateProps(resized);
  }

  /**
   * Used to copy some props in methods that returns new instance.
   *
   * @param instance Instance which props should be set.
   */
  protected duplicateProps(
    instance: ImageAdapter<ResourceType>
  ): ImageAdapter<ResourceType> {
    instance.setBackgroundColor(this.backgroundColor);
    return instance;
  }
}
